import { getColumnNameFromMonths } from "./regroupment";
import dayjs from "dayjs";
import "dayjs/locale/fr";

dayjs.locale("fr");

export const ROW_HEIGHT = 38;
export const FOOTER_HEIGHT = 71;

export const SPECIAL_HEADER_WIDTH = 190;
export const NORMAL_HEADER_WIDTH = 215;
export const NORMAL_CELL_WIDTH = 215;

export const HEADER_HEIGHT = 49;
export const LISTING_HEIGHT = 54;

export const SIDEBAR_WIDTH_EXPANDED = 259;
export const SIDEBAR_WIDTH_REDUCED = 100;

export const SCROLLBAR_WIDTH = 8;

function checkIfIsLocked(element, mode, project) {
  if (!project || !element) {
    return false;
  }

  const lockDate =
    mode === "treso" ? project.lockTresoDate : project.lockComptaDate;

  if (!lockDate) {
    return false; // No lock date set, element is not locked
  }

  const elementDate = mode === "treso" ? element.dateTreso : element.dateCompta;

  if (!elementDate) {
    return false; // No element date, cannot determine lock status
  }

  const elementDateObj = dayjs(elementDate); // ISO format: 2025-06-01T00:00:00.000Z
  const lockDateObj = dayjs(lockDate, "YYYY-MM");

  return (
    elementDateObj.isSame(lockDateObj, "month") ||
    elementDateObj.isBefore(lockDateObj, "month")
  );
}

function getIndividualMonthStatus(month, mode, project) {
  if (!project) {
    return "unlocked";
  }

  const lockDate =
    mode === "treso" ? project.lockTresoDate : project.lockComptaDate;

  if (!lockDate) {
    const monthDate = dayjs(month, "MMMM YYYY");
    const currentDate = dayjs();

    if (currentDate.isAfter(monthDate, "month")) {
      return "unlocked";
    } else {
      return "cannot be locked";
    }
  }

  const monthDate = dayjs(month, "MMMM YYYY");
  const lockDateObj = dayjs(lockDate, "YYYY-MM");
  const currentDate = dayjs();

  if (
    monthDate.isSame(lockDateObj, "month") ||
    monthDate.isBefore(lockDateObj, "month")
  ) {
    return "locked";
  } else if (currentDate.isAfter(monthDate, "month")) {
    return "unlocked";
  } else {
    return "cannot be locked";
  }
}

function getIndividualFotterMonthStatusForPopup(month) {
  if (!month) {
    return false;
  }

  const monthDate = dayjs(month, "MMMM YYYY");
  const currentDate = dayjs();

  return (
    monthDate.isSame(currentDate, "month") ||
    monthDate.isBefore(currentDate, "month")
  );
}

/** Month label to open in "soldes pointés" when a column spans several months */
function getLatestEligibleMonthForPointedPopup(months) {
  if (!months?.length) {
    return null;
  }

  const eligible = months.filter((m) =>
    getIndividualFotterMonthStatusForPopup(m)
  );

  if (!eligible.length) {
    return null;
  }

  return eligible.reduce((latest, m) => {
    const d = dayjs(m, "MMMM YYYY");
    const latestD = dayjs(latest, "MMMM YYYY");
    return d.isAfter(latestD, "month") ? m : latest;
  });
}

function getLockStatusForMonths(months, mode, project) {
  if (!months || months.length === 0) {
    return "unlocked";
  }

  const individualStatuses = months.map((month) =>
    getIndividualMonthStatus(month, mode, project)
  );

  // Apply grouping rules
  const uniqueStatuses = [...new Set(individualStatuses)];

  // Rule 1: Only unlocked months -> "unlocked" (green unlocked picto)
  if (uniqueStatuses.length === 1 && uniqueStatuses[0] === "unlocked") {
    return "unlocked";
  }

  // Rule 2: Only locked months -> "locked" (red locked picto)
  if (uniqueStatuses.length === 1 && uniqueStatuses[0] === "locked") {
    return "locked";
  }

  // Rule 3: Only non-lockable months -> "cannot be locked" (no picto)
  if (uniqueStatuses.length === 1 && uniqueStatuses[0] === "cannot be locked") {
    return "cannot be locked";
  }

  // Rule 4: Any mix -> "mixed" (green picto)
  return "mixed";
}

function getMonthsColumns(mode, monthsGrouped, RegroupmentValue, project) {
  const dateColumns = monthsGrouped.map((months, index, array) => {
    const lockStatus = getLockStatusForMonths(months, mode, project);

    return {
      title: getColumnNameFromMonths(months, RegroupmentValue),
      dataIndex: months.join(", "),
      key: index,
      width: NORMAL_CELL_WIDTH,
      type: "months",
      isSpecialColumn: false,
      className: mode === "compta" ? "th-compta-date" : "th-treso-date",
      isFirst: index === 0,
      isLast: index === monthsGrouped.length - 1,
      lockStatus: lockStatus,
      months: months,
    };
  });

  return dateColumns;
}

function getSpecialHeaderColumns(synthesis, objectif, isListing) {
  const commonCellProps = (col) => {
    return {
      type: col.cellName,
      width: col.cellName === "name" ? 240 : 210,
      className: `create-${col.cellType}`,
    };
  };

  const columns = [
    { cellName: "name", cellType: "category" },
    ...(objectif && !isListing
      ? [{ cellName: "objective", cellType: "objective" }]
      : []),
    ...(synthesis && !isListing
      ? [{ cellName: "synthese", cellType: "synthese" }]
      : []),
  ].map((col) => ({
    dataIndex: col.cellName,
    isSpecialColumn: true,
    key: col.cellName,
    ...commonCellProps(col),
  }));

  return columns;
}

const calculateLeft = (
  index,
  columns,
  scrollLeft,
  isSpecialColumn,
  isSidebarClosed,
  expandedColumns = {}
) => {
  const totalWidth = columns.slice(0, index).reduce((acc, col) => {
    const isExpandable =
      (col.type === "months" || col.type === "name") &&
      expandedColumns?.[col.dataIndex];
    const extraWidth = isExpandable ? NORMAL_HEADER_WIDTH : 0;
    return acc + col.width + extraWidth;
  }, 0);
  return isSpecialColumn ? totalWidth : `${totalWidth - scrollLeft}px`;
};

const calculateTableRowWidth = (columns) => {
  const totalWidth = columns?.reduce((acc, col) => acc + col.width, 0);
  return totalWidth;
};

const calculateCellBodyXPosition = (indexer, objectif, synthese, isListing) => {
  let objectifWidth = objectif ? 240 : 0;
  let syntheseWidth = synthese ? 240 : 0;
  const specialColumnsWidth = !isListing ? objectifWidth + syntheseWidth : 0;

  const totalWidth = indexer * NORMAL_HEADER_WIDTH + specialColumnsWidth + 240;
  return totalWidth;
};

const isCategoryToDefine = (category, isPlancompta) => {
  if (!isPlancompta) return false;

  return category?.index === 0 && category?.name === "A définir";
};

const getCategoryDragPrefix = (category, isPlancompta = false) => {
  if (category?.name) {
    const isToBeDefined =
      isPlancompta && isCategoryToDefine(category, isPlancompta);
    return `category=${category.id}&parent_id=${category.parentId}&isSpecial=${
      category.isSpecial
    }&identifier=${category.identifier}&levelId=${category.levelId}&index=${
      category.index
    }&isRacine=${!!category.isRacine}&isToBeDefined=${isToBeDefined}&isImported=${
      category?.isImported || false
    }&isProtected=${category?.isProtected || false}`;
  } else {
    return "";
  }
};

const getElementDragPrefix = (
  record,
  dataIndex,
  isListing,
  indexer,
  lockStatus,
  isPlancompta = false
) => {
  const element = record?.[dataIndex];

  if (!record[dataIndex] && !record.isEmpty && isListing) {
    return `indexer=${indexer}&dataIndex=${dataIndex}&lockStatus=${lockStatus}`;
  }

  if (record.name) {
    const isToBeDefined =
      isPlancompta && isCategoryToDefine(record, isPlancompta);
    return `category=${
      record?.isRacine ? record?.categoryId : record?.id
    }&isSpecial=${record?.isSpecial}&levelId=${
      record?.levelId
    }&dataIndex=${dataIndex}&indexer=${indexer}&isRacine=${!!record?.isRacine}&isToBeDefined=${isToBeDefined}&lockStatus=${lockStatus}&isImported=${
      record?.isImported || false
    }`;
  }

  const isToBeDefined =
    isPlancompta && isCategoryToDefine(record?.category, isPlancompta);

  return `category=${
    record?.category?.isRacine
      ? record.category.categoryId
      : record?.category?.id
  }&isSpecial=${record?.category?.isSpecial}&levelId=${
    record?.category?.levelId
  }&dataIndex=${dataIndex}&specialId=${record?.id}&elementId=${
    record?.[dataIndex]?.id
  }&type=${element?.status}&status=${
    element?.type
  }&indexer=${indexer}&isToBeDefined=${isToBeDefined}&lockStatus=${lockStatus}&isImported=${
    record?.isImported || false
  }&isLinked=${!!element?.linkedGroupId || false}&isDuplicated=${
    !!element?.isDuplicated || false
  }`;
};

function getCellType(Row, dataIndex) {
  const isEmptyRow = !!Row.isEmptyRow;
  const isEmptyCell = !Row[dataIndex] && !isEmptyRow;
  const hasPrice = !!Row[dataIndex] && !!Row.name;
  const hasElement = !!Row[dataIndex] && !Row.name;

  if (isEmptyRow) {
    return "emptyRow";
  } else if (isEmptyCell) {
    return "emptyCell";
  } else if (hasPrice) {
    return "price";
  } else if (hasElement) {
    return "element";
  } else {
    return "unknown";
  }
}

function calculateStickyPositions(columns, expandedColumns = {}) {
  const result = {};

  let currentPosition = 0;

  for (const column of columns) {
    const { key, width, type, dataIndex } = column;

    const isExpandable =
      (type === "months" || type === "name") &&
      expandedColumns?.[dataIndex];
    const effectiveWidth = isExpandable ? width + NORMAL_HEADER_WIDTH : width;

    result[key] = {
      position: currentPosition,
      className: column.className || "",
    };

    currentPosition += effectiveWidth;
  }

  return result;
}

const flattenCategoryTree = (expandedCategories, category) => {
  const result = [category];

  if (
    expandedCategories.includes(String(category?.id)) &&
    category.children &&
    category.children.length > 0
  ) {
    result.push(
      ...category.children.flatMap((child) =>
        flattenCategoryTree(expandedCategories, child)
      )
    );
  }

  return result;
};

function flatterCategoriesResponse(
  categories, 
  shouldShowMasked = false, 
  selectedMonths = [],
  isSynthesisActive = false,
  selectedMonthsSynthesis = []
) {
  if (!Array.isArray(categories)) return [];

  if (shouldShowMasked) {
    return categories;
  }

  // Helper function to check if category has elements in selected months
  function hasElementsInSelectedMonths(category, monthsToCheck) {
    const elementKeys = category.elements && typeof category.elements === 'object' 
      ? Object.keys(category.elements) 
      : [];
    
    if (elementKeys.length === 0) return false;
    
    if (monthsToCheck && monthsToCheck.length > 0) {
      // Check if any element key matches a selected month
      return elementKeys.some(key => monthsToCheck.includes(key));
    } else {
      // Fallback: if no months provided, check if elements object has any keys
      return elementKeys.length > 0;
    }
  }

  function hasElementsOrDescendantWithElements(category, forMaskedCategory = false) {
    const isMasked = category.isMasked === true || category.isMasked === 1;
    const isMaskedCategory = forMaskedCategory || isMasked;
    
    // For masked categories when synthesis is active: prioritize synthesis period
    if (isMaskedCategory && isSynthesisActive) {
      // Check if category has synthese data - if it does, it means there's data in synthesis period
      const hasSynthese = category.synthese != null && category.synthese !== 0;
      
      // Check if category or children have elements in synthesis period
      const hasElementsInSynthesis = hasElementsInSelectedMonths(category, selectedMonthsSynthesis);
      
      // Check children for synthesis period
      let childrenHaveElementsInSynthesis = false;
      let childrenHaveSynthese = false;
      if (Array.isArray(category.children) && category.children.length > 0) {
        childrenHaveElementsInSynthesis = category.children.some(child => {
          const childHasInSynthesis = hasElementsInSelectedMonths(child, selectedMonthsSynthesis);
          if (childHasInSynthesis) return true;
          // Recursively check grandchildren
          return hasElementsOrDescendantWithElements(child, true);
        });
        
        // Also check if any child has synthese data
        childrenHaveSynthese = category.children.some(child => 
          child.synthese != null && child.synthese !== 0
        );
      }
      
      // If has synthese data OR elements in synthesis period, show it (synthesis period takes precedence)
      if (hasSynthese || childrenHaveSynthese || hasElementsInSynthesis || childrenHaveElementsInSynthesis) {
        return true;
      }
      
      // Only if no synthese data AND no elements in synthesis period, check selected elements period
      const hasElements = hasElementsInSelectedMonths(category, selectedMonths);
      let childrenHaveElements = false;
      if (Array.isArray(category.children) && category.children.length > 0) {
        childrenHaveElements = category.children.some(child => {
          const childHasElements = hasElementsInSelectedMonths(child, selectedMonths);
          if (childHasElements) return true;
          return hasElementsOrDescendantWithElements(child, true);
        });
      }
      
      return hasElements || childrenHaveElements;
    }
    
    // For masked categories when synthesis is NOT active: check selected elements period only
    if (isMaskedCategory) {
      const hasElements = hasElementsInSelectedMonths(category, selectedMonths);
      if (hasElements) return true;
      
      // Check children recursively
      if (Array.isArray(category.children) && category.children.length > 0) {
        return category.children.some(child => {
          const childHasElements = hasElementsInSelectedMonths(child, selectedMonths);
          if (childHasElements) return true;
          return hasElementsOrDescendantWithElements(child, true);
        });
      }
      return false;
    }
    
    // For non-masked categories: check elements, synthese, or objectif
    const hasElements = hasElementsInSelectedMonths(category, selectedMonths);
    const hasSynthese = category.synthese != null;

    const hasObjectif = category.objectif?.amount != null;

    if (hasElements || hasSynthese || hasObjectif) return true;

    if (Array.isArray(category.children) && category.children.length > 0) {
      const childrenHaveElements = category.children.some(hasElementsOrDescendantWithElements);
      return childrenHaveElements;
    }

    return false;
  }

  function filterCategories(cats) {
    const result = [];

    for (const category of cats) {
      // If isForelements is true, always include it (with filtered children)
      if (category?.isInput === true) {
        result.push({
          ...category,
        });
      }
      if (category.isForelements === true) {
        let filteredChildren = [];
        if (Array.isArray(category.children) && category.children.length > 0) {
          filteredChildren = filterCategories(category.children);
        }
        result.push({
          ...category,
          children: filteredChildren,
        });
        continue;
      }

      // Check if category is masked (isMasked == true or 1)
      const isMaskedTrue =
        category.isMasked === true || category.isMasked === 1;
      const isMaskedZero = category.isMasked === 0;

      // For masked categories, check elements in selected range or synthesis period (if active)
      // For non-masked categories, check elements, synthese, or objectif
      const hasElementsInRange = hasElementsOrDescendantWithElements(category, isMaskedTrue);

      // If isMasked is exactly 0, always return it
      if (isMaskedZero || hasElementsInRange) {
        let filteredChildren = [];
        if (Array.isArray(category.children) && category.children.length > 0) {
          filteredChildren = filterCategories(category.children);
        }
        result.push({
          ...category,
          children: filteredChildren,
        });
        continue;
      }

      // If masked (true/1) and has no elements and no children with elements, skip this category
      if (isMaskedTrue && !hasElementsInRange) {
        continue;
      }

      // If the category has children, filter them recursively
      let filteredChildren = [];
      if (Array.isArray(category.children) && category.children.length > 0) {
        filteredChildren = filterCategories(category.children);
      }

      // Check if category has elements in the selected months
      const hasElements = hasElementsInSelectedMonths(category, selectedMonths);

      // If the category has elements, or any child (direct or indirect) has elements, include it
      if (hasElements || (filteredChildren && filteredChildren.length > 0)) {
        result.push({
          ...category,
          children: filteredChildren,
        });
      }
    }

    return result;
  }

  return filterCategories(categories);
}

function addRacineToCatgories(categories) {
  if (!Array.isArray(categories) || categories.length === 0) {
    return [];
  }

  const newCategories = [];

  categories.forEach((category) => {
    if (category.isRacine) {
      newCategories.push(category);
      return;
    }

    const noElements = Object.keys(category?.elements).length === 0;

    if (category.children.length === 0) {
      newCategories.push(category);
      return;
    }

    if (noElements) {
      category.children = addRacineToCatgories(category.children);
      newCategories.push(category);
      return;
    }

    const racineChild = {
      id: `${category.id}-racine`,
      categoryId: category.id,
      category: { ...category },
      key: `${category.id}-racine`,
      elements: { ...category?.elements },
      children: [],
      name: `racine de ${category?.name}`,
      isRacine: true,
      isSpecial: category?.isSpecial,
      levelId: category.levelId + 1,
      isMasked: category.isMasked,
    };

    const newCategory = {
      ...category,
      elements: {},
      children: [racineChild, ...addRacineToCatgories(category.children)],
    };

    newCategories.push(newCategory);
  });

  return newCategories;
}

function filterUnmaskedCategories(categories, id) {
  return categories
    .filter((cat) => {
      // If id is provided and this category is masked and id matches, keep it
      if (id !== undefined && cat.isMasked && cat.id === id) {
        return true;
      }
      // If id is provided and this category is masked but one of its descendants matches the id, keep it
      if (
        id !== undefined &&
        cat.isMasked &&
        cat.children &&
        Array.isArray(cat.children)
      ) {
        // Recursively check if any descendant has the id
        const hasDescendantWithId = (children) => {
          for (const child of children) {
            if (child.id === id) return true;
            if (child.children && Array.isArray(child.children)) {
              if (hasDescendantWithId(child.children)) return true;
            }
          }
          return false;
        };
        if (hasDescendantWithId(cat.children)) {
          return true;
        }
      }
      // Otherwise, only keep unmasked categories
      return !cat.isMasked;
    })
    .map((cat) => {
      if (cat.children && Array.isArray(cat.children)) {
        return {
          ...cat,
          children: filterUnmaskedCategories(cat.children, id),
        };
      }
      return { ...cat };
    });
}

const scrollManually = (rowsLength) => {
  const rowHeight = 40;

  const totalRowsHeight = rowsLength * rowHeight;

  const scrollPosition = totalRowsHeight - rowHeight;

  smoothScrollTo(scrollPosition);
};

const smoothScrollTo = (targetPosition) => {
  const duration = 500;
  const startTime = performance.now();
  const startScrollTop = document.querySelector(".table-scroll").scrollTop;

  const scrollStep = (timestamp) => {
    try {
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easedProgress = easeInOutQuad(progress);

      const newScrollTop =
        startScrollTop + (targetPosition - startScrollTop) * easedProgress;
      document.querySelector(".table-scroll").scrollTop = newScrollTop;

      if (progress < 1) {
        window.requestAnimationFrame(scrollStep);
      }
    } catch (error) {
      console.log(error);
    }
  };

  window.requestAnimationFrame(scrollStep);
};

const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

export {
  getMonthsColumns,
  getCategoryDragPrefix,
  scrollManually,
  getSpecialHeaderColumns,
  calculateLeft,
  getCellType,
  getElementDragPrefix,
  calculateStickyPositions,
  calculateTableRowWidth,
  calculateCellBodyXPosition,
  flattenCategoryTree,
  addRacineToCatgories,
  isCategoryToDefine,
  flatterCategoriesResponse,
  filterUnmaskedCategories,
  checkIfIsLocked,
  getIndividualFotterMonthStatusForPopup,
  getLatestEligibleMonthForPointedPopup,
};
